import { apiConfig } from '@config/api.config';

/**
 * Cliente para el endpoint streaming /api/v1/ai/chat/stream.
 *
 * Usa fetch + ReadableStream (no EventSource, porque EventSource no permite
 * POST con FormData ni cookies cross-origin reliably).
 *
 * Parsea SSE manualmente: `event: name\ndata: {...}\n\n`.
 *
 * Reenvía cada evento al callback `onEvent`. Termina cuando recibe `done`
 * o `error`, o cuando el stream se cierra.
 */

export interface RagSource {
  source_table: string;
  source_id: number;
  title: string;
  snippet: string;
  score: number;
}

export type StreamEvent =
  | { type: 'conversation'; id: number }
  | { type: 'stage'; stage: string; attempt?: number; count?: number }
  | { type: 'sources'; items: RagSource[] }
  | { type: 'sql'; sql: string }
  | { type: 'rows'; count: number }
  | { type: 'chart'; spec: Record<string, unknown> }
  | { type: 'token'; text: string }
  | {
      type: 'action';
      id: number;
      operation: 'insert' | 'update';
      target_table: string;
      preview: {
        operation: string;
        target_table: string;
        columns: Record<string, unknown>;
        where?: string;
      };
      sql: string;
      expires_at: string;
    }
  | { type: 'done'; message_id: number; duration_ms: number }
  | { type: 'error'; message: string };

export interface StreamChatPayload {
  message: string;
  conversation_id: number | null;
  files?: File[];
}

let csrfReady = false;

function readXsrfCookie(): string | null {
  const match = document.cookie.match(/(^|;\s*)XSRF-TOKEN=([^;]+)/);
  return match ? decodeURIComponent(match[2]) : null;
}

async function ensureCsrf(): Promise<void> {
  if (csrfReady) return;
  await fetch(`${apiConfig.sanctumBaseURL}/sanctum/csrf-cookie`, {
    credentials: 'include',
  });
  csrfReady = true;
}

export async function streamChat(
  payload: StreamChatPayload,
  onEvent: (ev: StreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  await ensureCsrf();

  const fd = new FormData();
  fd.append('message', payload.message);
  if (payload.conversation_id !== null) {
    fd.append('conversation_id', String(payload.conversation_id));
  }
  (payload.files ?? []).forEach((f) => fd.append('files[]', f));

  const xsrf = readXsrfCookie();

  const response = await fetch(`${apiConfig.baseURL}/ai/chat/stream`, {
    method: 'POST',
    body: fd,
    credentials: 'include',
    signal,
    headers: {
      Accept: 'text/event-stream',
      'X-Requested-With': 'XMLHttpRequest',
      ...(xsrf ? { 'X-XSRF-TOKEN': xsrf } : {}),
    },
  });

  if (!response.ok) {
    const text = await response.text();
    onEvent({ type: 'error', message: `HTTP ${response.status}: ${text.slice(0, 200)}` });
    return;
  }
  if (!response.body) {
    onEvent({ type: 'error', message: 'Sin body en la respuesta streaming.' });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE: eventos separados por doble \n\n
      let sepIdx: number;
      while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
        const rawEvent = buffer.slice(0, sepIdx);
        buffer = buffer.slice(sepIdx + 2);
        const parsed = parseSseEvent(rawEvent);
        if (parsed) onEvent(parsed);
      }
    }
  } catch (e: any) {
    if (e?.name !== 'AbortError') {
      onEvent({ type: 'error', message: e?.message ?? 'Stream interrumpido.' });
    }
  }
}

function parseSseEvent(raw: string): StreamEvent | null {
  let eventName = 'message';
  let data = '';
  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      data += line.slice(5).trim();
    }
  }
  if (!data) return null;
  try {
    const obj = JSON.parse(data);
    return { type: eventName as any, ...obj };
  } catch {
    return null;
  }
}
